import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Users, Gift, Heart, Wallet, FileText, AlertTriangle, CalendarRange, ShieldAlert,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import { getBenefitDashboard } from '../../services/benefitReportService';
import { formatDate } from '../../utils/Utils';
import PersonCell from '../../components/ui/PersonCell';

const toPascal = (v) => (v || '').split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');

// Sayfa boşluğunu Layout verir; panolar kendi kenar padding'ini EKLEMEZ
// (referans: DashboardPage / PdksDashboardPage).
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
`;

const KpiCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: all 0.15s;
  &:hover { ${({ $clickable }) => $clickable && 'transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,66,66,0.12);'} }
`;

const KpiIconBox = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg || '#E0F2FE'};
  color: ${({ $color }) => $color || '#0291EF'};
  flex-shrink: 0;
`;

const KpiLabel = styled.div`font-size: 12px; color: #64748b; margin-bottom: 2px;`;
const KpiValue = styled.div`font-size: 22px; font-weight: 800; color: #004242; line-height: 1.1;`;
const KpiSub = styled.div`font-size: 11px; color: #94a3b8; margin-top: 2px;`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const CardTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #004242;
  margin: 0 0 12px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  &:last-child { border-bottom: none; }
`;

const Pill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $danger }) => ($danger ? '#FEE2E2' : '#E0F2FE')};
  color: ${({ $danger }) => ($danger ? '#991B1B' : '#075985')};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 4px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${({ $color }) => $color || '#0291EF'};
  width: ${({ $pct }) => `${$pct}%`};
  transition: width 0.3s;
`;

const formatCurrency = (v) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(v || 0);

export default function BenefitDashboardPage() {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getBenefitDashboard()
      .then((res) => setData(res.data?.data))
      .finally(() => setLoading(false));
  }, []);

  const categoryChartData = useMemo(() => {
    if (!data?.costByCategory) return [];
    return data.costByCategory.slice(0, 8).map((c) => ({
      name: c.categoryName.length > 18 ? c.categoryName.substring(0, 16) + '…' : c.categoryName,
      cost: Number(c.monthlyCost) || 0,
      employees: c.employeeCount || 0,
    }));
  }, [data]);

  if (loading) return <Container>{t('common.loading')}</Container>;
  if (!data) return <Container>{t('common.noData')}</Container>;

  const ov = data.overview || {};

  return (
    <Container>
      <KpiGrid>
        <KpiCard>
          <KpiIconBox $bg="#E0F2FE" $color="#0291EF"><Users size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('benefitDashboard.activeEmployees')}</KpiLabel>
            <KpiValue>{ov.activeEmployees}</KpiValue>
          </div>
        </KpiCard>

        <KpiCard>
          <KpiIconBox $bg="#F0FDF4" $color="#16A34A"><Gift size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('benefitDashboard.activeBenefits')}</KpiLabel>
            <KpiValue>{ov.activeBenefits}</KpiValue>
          </div>
        </KpiCard>

        <KpiCard>
          <KpiIconBox $bg="#FEF3C7" $color="#F59E0B"><Heart size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('benefitDashboard.activeDependents')}</KpiLabel>
            <KpiValue>{ov.activeDependents}</KpiValue>
          </div>
        </KpiCard>

        <KpiCard>
          <KpiIconBox $bg="#ECFDF5" $color="#10B981"><Wallet size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('benefitDashboard.monthlyCompanyCost')}</KpiLabel>
            <KpiValue>{formatCurrency(ov.monthlyCompanyCost)}</KpiValue>
            <KpiSub>{t('benefitDashboard.employeeShare', { v: formatCurrency(ov.monthlyEmployeeCost) })}</KpiSub>
          </div>
        </KpiCard>

        <KpiCard $clickable onClick={() => history.push('/benefit-applications')}>
          <KpiIconBox $bg="#DBEAFE" $color="#2563EB"><FileText size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('benefitDashboard.pendingApplications')}</KpiLabel>
            <KpiValue>{ov.pendingApplications}</KpiValue>
            <KpiSub>{t('benefitDashboard.clickForDetails')}</KpiSub>
          </div>
        </KpiCard>

        <KpiCard $clickable onClick={() => history.push('/life-events')}>
          <KpiIconBox $bg="#FEE2E2" $color="#DC2626"><AlertTriangle size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('benefitDashboard.openLifeEvents')}</KpiLabel>
            <KpiValue>{ov.openLifeEvents}</KpiValue>
          </div>
        </KpiCard>

        <KpiCard $clickable onClick={() => history.push('/open-enrollment-periods')}>
          <KpiIconBox $bg="#F3E8FF" $color="#9333EA"><CalendarRange size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('benefitDashboard.activeOpenEnrollment')}</KpiLabel>
            <KpiValue>{ov.activeOpenEnrollmentPeriods}</KpiValue>
          </div>
        </KpiCard>

        <KpiCard $clickable onClick={() => history.push('/benefit-reports?tab=application-sla')}>
          <KpiIconBox $bg="#FEE2E2" $color="#DC2626"><ShieldAlert size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('benefitDashboard.sgkWarnings')}</KpiLabel>
            <KpiValue>{(data.sgkWarnings || []).length}</KpiValue>
            <KpiSub>{t('benefitDashboard.last5Days')}</KpiSub>
          </div>
        </KpiCard>
      </KpiGrid>

      <Grid2>
        <Card>
          <CardTitle>{t('benefitDashboard.costByCategory')}</CardTitle>
          {categoryChartData.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('common.noData')}</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={categoryChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, n) => n === 'cost' ? formatCurrency(v) : v} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="cost" name={t('benefitDashboard.monthlyCost')} fill="#02D3BF" />
                <Bar dataKey="employees" name={t('benefitDashboard.employees')} fill="#0291EF" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <CardTitle>{t('benefitDashboard.openEnrollmentProgress')}</CardTitle>
          {(data.openEnrollmentProgress || []).length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('benefitDashboard.noOpenEnrollment')}</div>
          ) : (
            data.openEnrollmentProgress.map((p) => {
              const pct = p.eligibleEmployees > 0
                ? Math.round((p.submittedSelections / p.eligibleEmployees) * 100)
                : 0;
              return (
                <div key={p.periodId} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, color: '#004242', fontSize: 13 }}>{p.name}</span>
                    <Pill>{t(`openEnrollment.status${toPascal(p.status)}`, p.status)}</Pill>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    {p.submittedSelections} / {p.eligibleEmployees} {t('benefitDashboard.submitted')} ({pct}%)
                  </div>
                  <ProgressBar>
                    <ProgressFill $pct={pct} $color={pct >= 80 ? '#16A34A' : pct >= 50 ? '#F59E0B' : '#0291EF'} />
                  </ProgressBar>
                </div>
              );
            })
          )}
        </Card>
      </Grid2>

      <Grid2>
        <Card>
          <CardTitle>{t('benefitDashboard.pendingApplicationsList')}</CardTitle>
          {(data.pendingApplications || []).length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('benefitDashboard.noPendingApplications')}</div>
          ) : (
            data.pendingApplications.slice(0, 8).map((a) => (
              <Row key={a.applicationId}>
                <div>
                  <div style={{ color: '#004242', fontWeight: 700 }}>
                    <PersonCell name={a.employeeName} profilePicture={a.employeeProfilePicture} size={24} />
                  </div>
                  <div style={{ color: '#64748b', fontSize: 12 }}>
                    {a.benefitName} • {t(`benefitApplication.type${toPascal(a.applicationType)}`, a.applicationType)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#94a3b8', fontSize: 11 }}>{formatDate(a.createDate, i18n.language)}</div>
                  {a.slaBreached && <Pill $danger>{t('benefitDashboard.slaBreached')}</Pill>}
                </div>
              </Row>
            ))
          )}
        </Card>

        <Card>
          <CardTitle>{t('benefitDashboard.recentLifeEvents')}</CardTitle>
          {(data.recentLifeEvents || []).length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('benefitDashboard.noRecentLifeEvents')}</div>
          ) : (
            data.recentLifeEvents.slice(0, 8).map((e) => (
              <Row key={e.lifeEventId}>
                <div>
                  <div style={{ color: '#004242', fontWeight: 700 }}>
                    <PersonCell name={e.employeeName} profilePicture={e.employeeProfilePicture} size={24} />
                  </div>
                  <div style={{ color: '#64748b', fontSize: 12 }}>
                    {t(`lifeEvent.type${toPascal(e.eventType)}`, e.eventType)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#94a3b8', fontSize: 11 }}>{formatDate(e.eventDate, i18n.language)}</div>
                  <Pill>{t(`lifeEvent.status${toPascal(e.status)}`, e.status)}</Pill>
                </div>
              </Row>
            ))
          )}
        </Card>
      </Grid2>

      <Card>
        <CardTitle>{t('benefitDashboard.sgkWarnings')}</CardTitle>
        {(data.sgkWarnings || []).length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('benefitDashboard.noSgkWarnings')}</div>
        ) : (
          data.sgkWarnings.map((w) => (
            <Row key={w.employeeBenefitId}>
              <div>
                <div style={{ color: '#004242', fontWeight: 700 }}>
                  <PersonCell name={w.employeeName} profilePicture={w.employeeProfilePicture} size={24} />
                </div>
                <div style={{ color: '#64748b', fontSize: 12 }}>{w.benefitName}</div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 12, color: '#94a3b8' }}>
                {formatDate(w.activationDate, i18n.language)}
              </div>
            </Row>
          ))
        )}
      </Card>
    </Container>
  );
}
